export type EntityId = number;

export interface SystemContext {
	world: World;
	delta: number;
	elapsed: number;
}

export type System = (context: SystemContext) => void;

export class World {
	private nextEntityId: EntityId = 1;
	private systems: System[] = [];
	private readonly stores = new Set<ComponentStore<any>>();
	private readonly resources = new Map<string, unknown>();
	private elapsed = 0;

	readonly entities = new Set<EntityId>();

	createEntity(): EntityId {
		const id = this.nextEntityId++;
		this.entities.add(id);
		return id;
	}

	destroyEntity(id: EntityId): void {
		if (!this.entities.has(id)) return;
		this.entities.delete(id);
		for (const store of this.stores) {
			store.handleEntityDestroyed(id);
		}
	}

	registerSystem(system: System): void {
		this.systems.push(system);
	}

	update(delta: number): void {
		if (delta <= 0 || this.systems.length === 0) return;
		this.elapsed += delta;
		const context: SystemContext = { world: this, delta, elapsed: this.elapsed };
		for (const system of this.systems) {
			system(context);
		}
	}

	setResource<T>(key: string, value: T): void {
		this.resources.set(key, value);
	}

	getResource<T>(key: string): T | undefined {
		return this.resources.get(key) as T | undefined;
	}

	deleteResource(key: string): void {
		this.resources.delete(key);
	}

	dispose(): void {
		const entities = Array.from(this.entities);
		for (const id of entities) {
			this.destroyEntity(id);
		}
		this.systems = [];
		this.resources.clear();
	}

	registerStore(store: ComponentStore<any>): void {
		this.stores.add(store);
	}
}

export class ComponentStore<TValue> {
	private readonly values = new Map<EntityId, TValue>();

	constructor(private readonly world: World) {
		this.world.registerStore(this);
	}

	set(entity: EntityId, value: TValue): void {
		this.values.set(entity, value);
	}

	get(entity: EntityId): TValue | undefined {
		return this.values.get(entity);
	}

	delete(entity: EntityId): void {
		this.values.delete(entity);
	}

	has(entity: EntityId): boolean {
		return this.values.has(entity);
	}

	clear(): void {
		this.values.clear();
	}

	forEach(callback: (value: TValue, entity: EntityId) => void): void {
		for (const [entity, value] of this.values) {
			callback(value, entity);
		}
	}

	entries(): IterableIterator<[EntityId, TValue]> {
		return this.values.entries();
	}

	handleEntityDestroyed(entity: EntityId): void {
		this.values.delete(entity);
	}
}
